import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { EvidenceHubFilters } from "@/components/evidence-hub/evidence-hub-filters";
import { EvidenceHubTable } from "@/components/evidence-hub/evidence-hub-table";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { useNursery } from "@/contexts/NurseryContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import { useEvidenceHubData } from "@/hooks/use-evidence-hub-data";
import {
  DEFAULT_SORT,
  detailRoute,
  EMPTY_FILTERS,
  type EvidenceSortColumn,
  hasActiveFilters,
  NO_MATCHES_MESSAGE,
  PAGE_SIZE,
  PICK_CATEGORY_MESSAGE,
  type SortDir,
} from "@/lib/evidence-hub";

function RecordCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-neutral-100 px-2 py-0.5 text-caption-bold font-caption-bold text-neutral-700">
      {count} {count === 1 ? "record" : "records"}
    </span>
  );
}

export function EvidenceHubPage() {
  const navigate = useNavigate();
  const { selectedNurseryId, nurseries, setSelectedNursery } = useNursery();

  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<EvidenceSortColumn | undefined>(
    undefined,
  );
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Resolve effective sort: explicit column, else newest completions first.
  const effectiveSort = sortColumn
    ? { column: sortColumn, dir: sortDir }
    : DEFAULT_SORT;

  // Inactive categories included: a record whose category was soft-disabled
  // would otherwise be unreachable, since it matches no visible option and is
  // not uncategorised either.
  const { data: categoriesResponse } = useListCategoriesQueryControllerList({
    includeInactive: true,
  });
  const categories = categoriesResponse?.data.items ?? [];

  const { items, total, isLoading } = useEvidenceHubData({
    nurseryId: selectedNurseryId ?? undefined,
    categoryId,
    sortBy: effectiveSort.column,
    sortDir: effectiveSort.dir,
    page,
    pageSize,
  });

  const handleCategoryChange = (next: string | undefined) => {
    setCategoryId(next);
    setPage(0);
  };

  // Categories are global, so the current filter survives a nursery switch.
  const handleNurseryChange = (nurseryId: string) => {
    const nursery = nurseries.find((n) => n.id === nurseryId);
    if (!nursery) return;
    setSelectedNursery(nursery.id, nursery.organisationId);
    setPage(0);
  };

  const handleReset = () => {
    setCategoryId(EMPTY_FILTERS.categoryId);
    setPage(0);
  };

  const handleSort = (column: EvidenceSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            Evidence Hub
          </span>
          <span className="text-body font-body text-subtext-color">
            View all compliance activity and evidence across your setting.
          </span>
        </div>
      </div>

      <div className="flex w-full flex-col items-start gap-4 pb-8">
        <div className="flex w-full items-center gap-3">
          <span className="text-heading-3 font-heading-3 text-default-font">
            All Activity
          </span>
          <RecordCountBadge count={total} />
        </div>

        <EvidenceHubFilters
          categories={categories}
          categoryId={categoryId}
          onCategoryChange={handleCategoryChange}
          nurseries={nurseries}
          selectedNurseryId={selectedNurseryId}
          onNurseryChange={handleNurseryChange}
          canReset={hasActiveFilters({ categoryId })}
          onReset={handleReset}
        />

        {/* Table. The prompt is checked before the spinner so picking no
            category never flashes a loading state. */}
        {!categoryId ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-subtext-color">
              {PICK_CATEGORY_MESSAGE}
            </span>
          </div>
        ) : isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex w-full items-center justify-center py-16">
            <span className="text-body font-body text-subtext-color">
              {NO_MATCHES_MESSAGE}
            </span>
          </div>
        ) : (
          <>
            <TableScrollContainer>
              <EvidenceHubTable
                items={items}
                activeColumn={effectiveSort.column}
                activeDir={effectiveSort.dir}
                onSort={handleSort}
                onView={(record) => navigate({ to: detailRoute(record) })}
              />
            </TableScrollContainer>

            <Pagination
              page={page + 1}
              pageSize={pageSize}
              totalCount={total}
              onPageChange={(next) => setPage(next - 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              itemNoun="results"
            />
          </>
        )}
      </div>
    </div>
  );
}

export default EvidenceHubPage;
